import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import BarcodeScanner from "@/components/BarcodeScanner";
import {
  UserPlus,
  MapPin,
  PackageCheck,
  QrCode,
  ScanLine,
  Receipt,
  PackagePlus,
  Truck,
  Zap,
  X,
} from "lucide-react";
import { toast } from "sonner";

/**
 * MobileQuickActions — a floating action button that opens a bottom sheet
 * with the 8 quick actions requested (Yeni Müşteri, Yeni Ziyaret, Üretim
 * Güncelle, Barkod, QR, Satın Alma, Malzeme Kabul, Sevkiyat). Visible on
 * mobile viewports only.
 */
export default function MobileQuickActions() {
  const [open, setOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const navigate = useNavigate();

  const go = (path) => { setOpen(false); navigate(path); };

  const captureLocation = () => {
    setOpen(false);
    if (!("geolocation" in navigator)) {
      toast.error("Bu cihaz GPS desteklemiyor");
      return;
    }
    toast.loading("Konum alınıyor…", { id: "geo" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        toast.success(`Konum kaydedildi (±${Math.round(accuracy)}m)`, { id: "geo" });
        // Open Google Maps directions from device
        const url = `https://www.google.com/maps/@${latitude},${longitude},16z`;
        window.open(url, "_blank", "noopener");
      },
      (err) => {
        toast.error(`Konum alınamadı: ${err.message}`, { id: "geo" });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const openScanner = () => {
    setOpen(false);
    setScannerOpen(true);
  };

  const ACTIONS = [
    { label: "Yeni Müşteri", icon: UserPlus, onClick: () => go("/musteri"), testId: "qa-new-customer", color: "bg-blue-600" },
    { label: "Yeni Ziyaret (GPS)", icon: MapPin, onClick: captureLocation, testId: "qa-new-visit", color: "bg-blue-600" },
    { label: "Üretim Güncelle", icon: PackageCheck, onClick: () => go("/uretim"), testId: "qa-update-production", color: "bg-emerald-600" },
    { label: "Barkod Tara", icon: ScanLine, onClick: openScanner, testId: "qa-scan-barcode", color: "bg-blue-600" },
    { label: "QR Tara", icon: QrCode, onClick: openScanner, testId: "qa-scan-qr", color: "bg-blue-600" },
    { label: "Satın Alma Emri", icon: Receipt, onClick: () => go("/satinalma"), testId: "qa-new-purchase", color: "bg-slate-700" },
    { label: "Malzeme Kabul", icon: PackagePlus, onClick: () => go("/satinalma"), testId: "qa-receive-material", color: "bg-emerald-600" },
    { label: "Sevkiyat", icon: Truck, onClick: () => go("/uretim"), testId: "qa-dispatch", color: "bg-slate-700" },
  ];

  return (
    <>
      <button
        data-testid="mobile-fab"
        onClick={() => setOpen(true)}
        className="lg:hidden fixed bottom-5 right-5 z-40 w-14 h-14 bg-blue-600 hover:bg-blue-500 rounded-full shadow-2xl shadow-blue-600/40 flex items-center justify-center text-white transition-colors"
        aria-label="Hızlı İşlemler"
      >
        <Zap className="w-6 h-6" strokeWidth={2.5} />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="bg-slate-950 border-t border-slate-800 text-slate-100 rounded-t-sm p-0 max-h-[80vh]"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-800">
            <SheetTitle className="text-slate-50 tracking-tight flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-500" />
              Hızlı İşlemler
            </SheetTitle>
            <div className="text-xs text-slate-400 mt-0.5">Sık yapılan işlemler için mobil kısayollar</div>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-3 p-4 pb-8 overflow-y-auto">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.label}
                  data-testid={a.testId}
                  onClick={a.onClick}
                  className="bg-slate-900 border border-slate-800 hover:border-blue-600 active:bg-slate-800 rounded-sm p-4 flex flex-col items-start gap-3 transition-colors touch-manipulation"
                >
                  <div className={`w-10 h-10 ${a.color} rounded-sm flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="text-sm font-semibold text-slate-100 text-left leading-tight">
                    {a.label}
                  </div>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      <BarcodeScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onDetected={(code) => {
          // Route to inventory scan handler in a future iteration.
          // For now the toast in the scanner confirms detection.
          console.log("Scanned:", code);
        }}
      />
    </>
  );
}
